import React, { useCallback } from 'react'
import {
  Control,
  FieldValues,
  Path,
  useController,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Select from './Select'
import { SelectProps } from './SelectProps'

export type FormSelectProps<
  T extends FieldValues = FieldValues,
  OptionT = string | undefined
> = {
  name: Path<T>
  control: Control<T>
} & Omit<SelectProps<OptionT>, 'value' | 'onChange' | 'onBlur' | 'errorMessage'>

const FormSelect = <
  T extends FieldValues = FieldValues,
  OptionT = string | undefined
>({
  name,
  control,
  ...props
}: FormSelectProps<T, OptionT>) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const { t } = useTranslation(['validation'])
  const handleChange = useCallback(
    (value: OptionT) => {
      field.onChange(value)

      if (error) {
        field.onBlur()
      }
    },
    [error, field]
  )

  return (
    <Select
      {...props}
      onChange={handleChange}
      onBlur={field.onBlur}
      value={field.value}
      errorMessage={error?.message ? t(error.message) : undefined}
    />
  )
}

export default FormSelect
